// React Router
import { Navigate, Route, Routes } from "react-router-dom";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// Plugin System
import { createPluginStore, PluginProvider } from "react-pluggable";
import { PaymentMethodsPlugin } from "@/plugins";

// Components - Using barrel exports
import {
  // Layout & Common
  DashboardLayout,
  ProtectedRoute,
  Loader,
  IdleLogoutHandler,
  NotFoundPage,

  // App Components
  AuthenticationPage,
  ForgotPassword,
  ResetPassword,
  CreatePassword,
  StaffQRCodePage,
  QRCheckInPage,
  PrivacyPolicy,
  TermsAndConditions,
  PaymentGateway,
  Success,
  PaymentSuccessPage,
  PaymentProcessPage,
  PaymentFailedPage,

  // Admin Components
  Dashboard,
  MemberPage,
  BusinessDetail,
  Staff,
  Coach,
  Exercise,
  WorkoutPlan,
  WorkoutPlanForm,
  WorkoutStep1,
  WorkoutStep2,
  FoodsNutrition,
  MealPlans,
  OrganizationInvoices,
  Events,

  // Check In Module
  CheckIn,
  CheckInLogs,

  // Wallet Module
  WalletRequest,
  WalletRefund,

  // System Configuration Module
  SaleTaxes,
  IncomeCategory,
  TaxGroup,
  Memberships,
  Credits,
  RolesAndAccess,
  Products,
  CategoriesPage,
  PaymentMethods,
  CounterManagement,
  HardwareIntegration,
  SystemConfiguration,

  // POS Module
  CounterSelection,
  Sell,
  CashManagement,
  Register,
  SaleHistory,
  DHAccounting,

  Consultation,

  // Report Components
  SalesReport,
  CashReport,

  // Facility Module
  Facilities,
  FacailityBooking,
  FacilitySchedule,
  ManageBookingTable,
  AddonReturns,
  BusinessUnit,
  SRBReport,
  DonationReport,
  OtherReport,
} from "@/components";
import PaymentReport from "./components/admin/payment_report";
import RevenueReport from "./components/admin/revenue_report";
import ForeeReport from "./components/admin/foree_report";


const pluginStore = createPluginStore();
pluginStore.install(new PaymentMethodsPlugin());


function App() {
  const loading = useSelector((state: RootState) =>
    Object.values(state.api.queries).some(
      (query) => query && query.status === "pending"
    )
  );


  return (
    <>
      <PluginProvider pluginStore={pluginStore}>
        <IdleLogoutHandler />
        <Routes>
          {/* Public Routes */}
          <Route path="/reset_password/:token" element={<ResetPassword />} />
          <Route path="/create_password/:token" element={<CreatePassword />} />
          <Route path="/forgot_password" element={<ForgotPassword />} />
          <Route path="/qr-code" element={<StaffQRCodePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/payment" element={<PaymentGateway />} />
          <Route path="/payment/process" element={<PaymentProcessPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/failed" element={<PaymentFailedPage />} />
          <Route path="/checkin" element={<QRCheckInPage />} />
          <Route path="/success" element={<Success />} />


          <Route path="/" element={<ProtectedRoute />}>
            <Route path="/" index element={<AuthenticationPage />} />
            <Route element={<DashboardLayout />}>
              <Route path="/admin/dashboard" element={<Dashboard />} />
              <Route path="/admin/members" element={<MemberPage />} />
              <Route
                path="/admin/members/detail/:memberId?"
                element={<BusinessDetail />}
              />
              <Route path="/admin/coach" element={<Coach />} />
              <Route path="/admin/staff" element={<Staff />} />

              <Route path="/admin/mealplans" element={<MealPlans />} />
              <Route path="/admin/foods" element={<FoodsNutrition />} />

              <Route path="/admin/exercise" element={<Exercise />} />
              <Route path="/admin/workoutplans/" element={<WorkoutPlan />}>
                <Route path="add/" element={<WorkoutPlanForm />}>
                  <Route path="step/1/:workoutId?" element={<WorkoutStep1 />} />
                  <Route path="step/2/:workoutId?" element={<WorkoutStep2 />} />
                </Route>
              </Route>
              <Route
                path="/admin/finances"
                element={<OrganizationInvoices />}
              />
              <Route path="/admin/consultation" element={<Consultation />} />


              {/* Facility Module */}
              <Route path="/admin/facility" element={<Facilities />} />
              <Route
                path="/admin/facility/schedule"
                element={<FacilitySchedule />}
              />
              <Route
                path="/admin/facility/booking"
                element={<FacailityBooking />}
              />
              <Route
                path="/admin/facility/booking-management"
                element={<ManageBookingTable />}
              />
              <Route
                path="/admin/facility/addon-returns"
                element={<AddonReturns />}
              />
              <Route path="/admin/event" element={<Events />} />

              {/* System Settings */}
              <Route path="/admin/saleTaxes" element={<SaleTaxes />} />
              <Route
                path="/admin/incomeCategory"
                element={<IncomeCategory />}
              />
              <Route path="/admin/tax-group" element={<TaxGroup />} />
              <Route path="/admin/memberships" element={<Memberships />} />
              <Route path="/admin/credits" element={<Credits />} />
              <Route path="/admin/products" element={<Products />} />
              <Route path="/admin/categories" element={<CategoriesPage />} />
              <Route path="/admin/roles" element={<RolesAndAccess />} />
              <Route
                path="/admin/hardware_integration"
                element={<HardwareIntegration />}
              />
              <Route path="/admin/counter" element={<CounterManagement />} />
              <Route
                path="/admin/paymentMethods"
                element={<PaymentMethods />}
              />
              <Route
                path="/admin/system_configuration"
                element={<SystemConfiguration />}
              />
              <Route path="/admin/business-unit" element={<BusinessUnit />} />


              {/* POS Module */}
              <Route path="/admin/pos/">
                <Route path="sell/:id?" element={<Sell />} />
                <Route path="salesReport" element={<SaleHistory />} />
                <Route path="register" element={<Register />} />
                <Route path="cash" element={<CashManagement />} />
              </Route>

              <Route path="/admin/dh-accounting" element={<DHAccounting />} />


              {/* Wallet Module */}
              <Route path="/admin/wallet-request" element={<WalletRequest />} />
              <Route path="/admin/refund-request" element={<WalletRefund />} />

              {/* Check In Module */}
              <Route path="/admin/check-in-logs" element={<CheckInLogs />} />


              {/* Report Module */}
              <Route path="/admin/report/sales" element={<SalesReport />} />
              <Route path="/admin/report/daily-cash" element={<CashReport />} />
              <Route path="/admin/report/payment" element={<PaymentReport />} />
              <Route path="/admin/report/revenue" element={<RevenueReport />} />
              <Route path="/admin/report/foree" element={<ForeeReport />} />
              <Route path="/admin/report/srb" element={<SRBReport />} />
              <Route path="/admin/report/donation" element={<DonationReport />} />
              <Route path="/admin/report/other" element={<OtherReport />} />
            </Route>

            <Route path="/notfound" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/notfound" replace />} />
            <Route path="/admin/check-in" element={<CheckIn />} />
          </Route>
          <Route path="/counter-selection" element={<CounterSelection />} />
        </Routes>
        <Loader open={loading} />
      </PluginProvider>
    </>
  );
}

export default App;